import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useIntl } from 'umi';
import CardSlot from '@/pages/dashboard/components/CardSlot';
import { Select } from 'tea-component';
import { formatFilterValues, handleDownload, otherParamsVal } from '@/pages/dashboard/utils/utils';
import { useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { getPcConsoleRevenueTree } from '@/pages/dashboard/services/monetization';
import { useTableColumns, useTableDownLoad, useTreeData } from './hooks';
import TreeComponent from '@/pages/dashboard/components/TreeComponent';
import './index.scss';
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";

const SalesBreakdownTreeChart = (props: any) => {
  const { gameConfig, gameCode, showLifeTimeRefund = false, selectedMetrics, config } = props;
  const { otherParams } = config || {};

  const {name, nameCn, resName}= selectedMetrics; 
  const { locale } = useIntl();
  const isEn = locale === 'en-US';
  const [action, setAction] = useState('tere-icon');
  const [tableData, setTableData] = useState([]);
  const [FilterTableData, setFilterTableData] = useState([]);
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { pagePath, selector, timeType } = filterVal || {};
  const expendedDataRef = useRef([]);
  const parentElementRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { formatMessage } = useIntl();
  const [childWidth, setChildWidth] = useState<number>(120);
  const default_select = gameConfig?.page_selector?.find(
    (item: any) => item?.path === '/monetization/salesOverview/breakdown',
  )?.filters;
  const isHavePageDefaultSelector = default_select?.length > 0;
  // 产品确认默认值为system、product
  const [leftValue, setLeftValue] = useState<string>('system');
  const [rightValue, setRightValue] = useState('product');
  let selectOptions = useSelectArrObj(filterVal, !isHavePageDefaultSelector, false, false, pagePath);
  if (!isHavePageDefaultSelector) {
    // 前端默认是不展示'zone','channel','language的，所以当使用前端配置的时候，过滤掉这三个
    selectOptions = selectOptions?.filter((item: { value: string; }) => !['zone', 'channel', 'language']?.includes(item?.value));
  }
  selectOptions = selectOptions?.filter(
    (item: { groupKey: string; value: string; }) =>
      item?.groupKey === 'separated',
  );

  const { leftOptions, rightOptions } = useMemo(() => {
    // 左右两个筛选器互斥，左边有的选项，右边不能有，且category和region筛选器比较特殊，左边的只能是右边的父级，
    // 当右边选分区域或者分产品类型时，左边分区域、分国家、分产品类型、分产品的选项都没有
    const leftOptions = selectOptions?.filter((item: any) => {
      if (rightValue === 'region') {
        return item.value !== rightValue && item.value !== 'country';
      }
      if (rightValue === 'category') {
        return item.value !== rightValue && item.value !== 'product';
      }
      return item.value !== rightValue;
    });

    const rightOptions = selectOptions?.filter((item: { value: string; }) => {
      if (leftValue === 'country') {
        return item.value !== leftValue && item.value !== 'region';
      }
      if (leftValue === 'product') {
        return item.value !== leftValue && item.value !== 'category';
      }
      return item.value !== leftValue;
    });
    return { leftOptions, rightOptions };
  }, [selectOptions, leftValue, rightValue]);
  
  const title = isEn ? name : nameCn;

  const treeData = useTreeData(
    selectedMetrics,
    selector,
    tableData,
    leftValue,
    rightValue,
    title,
    expendedDataRef?.current,
  );

  const columns = useTableColumns(
    {
      selectedMetrics,
      leftValue,
      rightValue,
      selector,
      title,
      sumValue: treeData?.sumValue
    }
  );

  const tableDownloadConfig = useTableDownLoad(
    selectedMetrics,
    columns,
    FilterTableData,
    leftValue,
    rightValue,
    treeData?.sumValue
  );

  // 获取第二列的宽度
  useEffect(() => {
    const observerCallback = (mutations: any, observer: any) => {
      const firstEle = parentElementRef.current?.querySelector('.dbd-tree-node-depth-1  .dbd-tree-node-content') as HTMLElement;
      const secEle = parentElementRef.current?.querySelector('.dbd-tree-node-depth-2  .dbd-tree-node-content') as HTMLElement;
      const thiEle = parentElementRef.current?.querySelector('.dbd-tree-node-depth-3  .dbd-tree-node-content') as HTMLElement;

      const syncWidth = (ref: HTMLElement | null, target: HTMLElement | null, offset = 0) => {
        if (ref) {
          if (target) {
            target.style.width = ref.offsetWidth - offset + 'px';
          }
        }
      }
      syncWidth(firstEle, document?.querySelector('.dbd-pc-console-revenue-select-box-placeholder'), 0);
      syncWidth(secEle, document?.querySelector('.dbd-pc-console-revenue-select-box-left'), 0);
      syncWidth(thiEle, document?.querySelector('.dbd-pc-console-revenue-select-box-right'), 20);
    };

    const observerOptions = {
      childList: true, // 观察目标子节点的变化
      subtree: true, // 观察目标所有后代节点的变化
    };

    const observer = new MutationObserver(observerCallback);
    const currentElement = parentElementRef.current;

    if (currentElement) {
      // 开始观察目标节点
      observer.observe(currentElement, observerOptions);
    }

    const resizeObserver = new ResizeObserver(observerCallback);
    resizeObserver.observe(document.documentElement);

    return () => {
      // 组件卸载时停止观察
      observer.disconnect();
      resizeObserver.disconnect();
    };
    // 添加loading监听，当不改变筛选器点击筛选器确认按钮时也触发useEffect，重新设置宽度，防止分支图-筛选器位置偏移
  }, [treeData, action,loading]);

  const iconList = [
    {
      icon: 'tere-icon',
      context: formatMessage({ id: 'common_Tree' }),
    },
    {
      icon: 'sheet',
      context: formatMessage({ id: 'common_table' }),
    },
  ];

  useEffect(() => {
    if (filterLoading) {
      return;
    }
    getTableData();
  }, [filterLoading, filterVal, selectedMetrics?.resName]);



  const getTableData = (
    leftValueReq = leftValue,
    rightValueReq = rightValue,
  ) => {

    const { rangeTime, ...filters } = filterVal || {};
    const accumulated = [leftValueReq, rightValueReq];
    const params = {
      game_code: gameCode,
      start_date: rangeTime?.[0],
      end_date: rangeTime?.[1],
      filters: formatFilterValues(filters),
      accumulation: {
        not_by_date: true,
        by_zone: accumulated.includes('zone'),
        by_region: accumulated.includes('region'),
        by_country: accumulated.includes('country'),
        by_channel: accumulated.includes('channel'),
        by_system: accumulated.includes('system'),
        by_language: accumulated.includes('language'),
        by_category: accumulated.includes('category'),
        by_product: accumulated.includes('product'),
      },
      period: timeType ? PERIODTIMEOBJ[timeType] : "daily",
      desc: true,
      sorted_by: 'date',
      metrics: [resName],
      page: 1,
      page_size: 10000,
    };
    setLoading(true);
    getPcConsoleRevenueTree({...params, ...(otherParamsVal(otherParams, formatFilterValues(filters)) || {})})
      .then((result: any) => {
        setLoading(false);
        setError(false);
        setTableData(result?.metrics_list || []);
        const filteredList = result?.metrics_list.filter((item: { metrics: { [x: string]: any; }; }) => {
          const metricsItem = item.metrics?.[resName];
          return parseFloat(metricsItem?.value) !== 0 && metricsItem?.value !== '';
        });
        setFilterTableData(filteredList || []);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };
  const onDownload = () => {
    handleDownload(
      {
        option: tableDownloadConfig?.downloadOptions,
        data: tableDownloadConfig?.dowLoadData,
      },
      title,
      false,
    );
  };

  const getSelectedNode = (treeNodeData: { children: { filter: (arg0: (item: any) => boolean) => never[]; }; }) => {
    expendedDataRef.current = treeNodeData?.children?.filter((item) => !item?.collapsed) || [];
  };

  return (
    <CardSlot
      className="dbd-sumNetRevenue"
      title={title}
      activeTab={action}
      isEyes={true}
      iconShow={loading || error || tableData?.length === 0}
      onDownload={onDownload}
      getChange={(value: any) => {
        setAction(value);
      }}
      iconList={iconList}
    >
      <Abnormal
        style={{ height: 600 }}
        isLoading={loading}
        isError={error}
        isNoData={tableData?.length === 0}
        component={
          <>
            {action === 'tere-icon' ? (
              <div className="dbd-sumNetRevenue-tree">
                <div className="dbd-pc-console-revenue-select-box">
                  <div className='dbd-pc-console-revenue-select-box-placeholder'></div>
                  <Select
                    listHeight={226}
                    listWidth={210}
                    className="dbd-pc-console-revenue-select-box-left"
                    appearance="button"
                    options={leftOptions}
                    value={leftValue}
                    onChange={(value) => {
                      setLeftValue(value);
                      getTableData(value, rightValue);
                    }}
                  />
                  <Select
                    listHeight={226}
                    listWidth={210}
                    className="dbd-pc-console-revenue-select-box-right"
                    // style={childWidth ? { marginLeft: `calc(${childWidth}px - 440px + 15px + 2vw + 3.125vw + 15px )` } : undefined}
                    appearance="button"
                    options={rightOptions}
                    value={rightValue}
                    onChange={(value) => {
                      setRightValue(value);
                      getTableData(leftValue, value);
                    }}
                  />
                </div>
                <div ref={parentElementRef}>
                  <TreeComponent
                    isSingleExpands={true}
                    getSelectedNode={getSelectedNode}
                    data={treeData}
                    styleConfig={{
                      width: 16.5,
                      height: 8,
                      backgroundColor: '#ecebea',
                    }}
                  />
                </div>
              </div>
            ) : (
              <MiniTable
                className="dbd-sumNetRevenue-table"
                height={340}
                loading={false}
                tableOption={columns}
                tableList={FilterTableData}
                scrolLableVal={{
                  virtualizedOptions: {
                    height: 305,
                    itemHeight: 50,
                  },
                }}
              />
            )}
          </>
        }
      />
    </CardSlot>
  );
};

export default connect((state: any) => ({
  gameConfig: state.dashboardSetting.gameConfig,
  dataFrame: state.dashboard?.currentGame?.data_frame || 'mobile',
  gameCode: state.dashboard?.currentGame?.game_code,
}))(SalesBreakdownTreeChart);
